import { inject, InjectionToken } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import pouchdbDebug from 'pouchdb-debug';
import { addRxPlugin, createRxDatabase, RxDatabase } from 'rxdb';
import makeDebug from 'src/makeDebug';
import { AccountService } from '../../account.service';
import { EnvironmentService } from '../../environment/environment.service';
import {
  channelsMigrationStrategies,
  channelsSchema,
  chatChannelConsumptionSchema,
  ChatCollections,
  membersMigrationStrategies,
  membersSchema,
  messagesMigrationStrategies,
  messagesSchema,
} from './db-schema';

const debug = makeDebug('services:chat:db');

export const RX_DB_SERVICE_TOKEN = new InjectionToken<Promise<RxDatabase<ChatCollections>>>(
  'Manually constructed MyService',
  {
    providedIn: 'root',
    factory: () => _initDb(inject(Platform), inject(AccountService), inject(EnvironmentService)),
  }
);

export async function _initDb(
  platform: Platform,
  accountService: AccountService,
  environmentService: EnvironmentService
) {
  if (Capacitor.isNativePlatform()) {
    // RxDB is currently only used by chat and chat is a web feature
    // Prevent unnecessary DB initialization on native platform
    return Promise.resolve(null);
  }

  addRxPlugin(require('pouchdb-adapter-idb'));
  addRxPlugin(pouchdbDebug);
  addRxPlugin(require('rxdb/plugins/watch-for-changes'));

  debug('init db');
  const isCordova = platform.is('hybrid');
  const pouchSettings: any = {
    revs_limit: 1,
  };

  // migration - see rx-db-migration.spec.ts (to migrate schema)
  // https://github.com/pubkey/rxdb/blob/master/docs-src/data-migration.md
  // Use for dev
  // await RxDB.removeDatabase('chatdb', 'idb');

  const db = await createRxDatabase<ChatCollections>({
    name: await createDbName(accountService, environmentService),
    adapter: 'idb',
    ignoreDuplicate: true,
    pouchSettings,
  });
  await db.addCollections({
    channels: {
      schema: channelsSchema,
      migrationStrategies: channelsMigrationStrategies,
    },
    consumptions: {
      schema: chatChannelConsumptionSchema,
    },
    messages: {
      schema: messagesSchema,
      migrationStrategies: messagesMigrationStrategies,
    },
    members: {
      schema: membersSchema,
      migrationStrategies: membersMigrationStrategies,
    },
  });
  debug('db init completed');
  // setup watch for changes when writing directly with .pouch
  db.messages.watchForChanges();
  return db;
}

/**
 * Waits for the account, then derives the name; an unusable account rejects instead of leaving the
 * chat database uninitialised without a trace (#26615). Same rule as DatabaseFactory: no fallback
 * name is invented.
 */
export async function createDbName(accountService: AccountService, environmentService: EnvironmentService) {
  const account = await accountService.accountReady;
  const branch = environmentService.isProductionBranch() ? 'master' : environmentService.branch;
  const dbName = `${account.firstName.trim()[0]}${account.lastName.trim()[0]}${account._id.slice(-4)}_${branch}`;
  return normalizeToASCII(dbName.toLowerCase());
}

const normalizeToASCII = (str: string) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // replace diacritics
    .replace(/\u00E6/g, 'a') // replace æ
    .replace(/\u00F8/g, 'o'); // replace ø
};
